import type { GearSlot, Rarity, SlotId, SubSpecId, TargetKind, Condition } from '../model/types';
import type { GameController, TabId, WatchSpeed } from './controller';
import type { BagFilter } from './bagView';
import { mountFeedbackDialog } from './feedbackDialog';
import { renderModal, renderPanel, renderStatus } from './panels';

export interface ShellNodes {
  status: HTMLElement;
  panel: HTMLElement;
  modal: HTMLElement;
  tabs: HTMLElement;
  feedback: HTMLElement;
}

/** Column gap under the header. The mobile rules sheet starts this far below the measured bar. */
export const RULES_SHEET_GAP_PX = 12;

/** `top` for the mobile rules sheet: one column-gap under the header's real bottom edge. */
export function rulesSheetTop(headerBottomPx: number): string {
  return `${Math.ceil(headerBottomPx + RULES_SHEET_GAP_PX)}px`;
}

/** Writes --topbar-bottom from #topbar while the viewport is the mobile layout. Desktop leaves it unset. */
export function syncRulesSheetTop(doc: Document = document): void {
  const root = doc.documentElement;
  const mobile = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 860px)').matches;
  const header = doc.querySelector<HTMLElement>('#topbar');
  if (!mobile || !header) {
    root.style.removeProperty('--topbar-bottom');
    return;
  }
  const bottom = header.getBoundingClientRect().bottom;
  if (bottom <= 0) {
    root.style.removeProperty('--topbar-bottom');
    return;
  }
  root.style.setProperty('--topbar-bottom', `${Math.ceil(bottom)}px`);
}

/** First repeat wait. Later waits shrink until SPEND_HOLD_MIN_MS. */
export const SPEND_HOLD_START_MS = 280;
export const SPEND_HOLD_MIN_MS = 45;

/** Wire the shell. The report dialog lives in `feedback`, never inside the combat panel. */
export function mountShell(controller: GameController, nodes: ShellNodes): () => void {
  const { status, panel, modal, tabs, feedback } = nodes;
  const unmountDialog = mountFeedbackDialog(feedback, controller);

  const draw = (scope: 'all' | 'status' = 'all'): void => {
    renderStatus(controller, status);
    const freezeCombat = controller.feedbackOpen && controller.tab === 'combat';
    if (scope === 'all' && !freezeCombat) {
      const panelScroll = panel.scrollTop;
      const rulesScroll = panel.querySelector<HTMLElement>('.rules-screen')?.scrollTop ?? 0;
      const openSpecs = [...panel.querySelectorAll<HTMLElement>('details.spec-more[open]')].map((node) => node.dataset.spec ?? '');
      const active = document.activeElement;
      const keepId =
        controller.rulesOpen && active instanceof HTMLElement && panel.contains(active) && active.id ? active.id : '';
      renderPanel(controller, panel);
      for (const id of openSpecs) {
        if (!id) continue;
        panel.querySelector(`details.spec-more[data-spec="${id}"]`)?.setAttribute('open', '');
      }
      const restoreScroll = (): void => {
        panel.scrollTop = panelScroll;
        const rules = panel.querySelector<HTMLElement>('.rules-screen');
        if (rules) rules.scrollTop = rulesScroll;
      };
      restoreScroll();
      if (controller.rulesFocus === 'back') panel.querySelector<HTMLElement>('#rules-back')?.focus({ preventScroll: true });
      else if (controller.rulesFocus === 'open') panel.querySelector<HTMLElement>('#open-rules')?.focus({ preventScroll: true });
      else if (keepId) panel.querySelector<HTMLElement>(`#${keepId}`)?.focus({ preventScroll: true });
      restoreScroll();
      controller.rulesFocus = null;
    }
    renderModal(controller, modal);
    tabs.querySelectorAll('button').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.tab === controller.tab);
    });
    syncRulesSheetTop();
  };

  const unsub = controller.subscribe(draw);
  draw();

  const onTab = (event: Event): void => {
    const button = (event.target as HTMLElement).closest('button');
    const tab = button?.dataset.tab;
    if (tab === 'combat' || tab === 'hero' || tab === 'build') controller.setTab(tab as TabId);
  };
  const onPanelClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (target.closest('#combat-result')) {
      controller.retryQueuedClear();
      return;
    }
    const button = target.closest<HTMLElement>('[data-action]');
    if (!button || button.tagName === 'SELECT' || button.tagName === 'INPUT') return;
    const action = button.dataset.action;
    const index = Number(button.dataset.index ?? '-1');
    if (action === 'pace') controller.setWatchSpeed(Number(button.dataset.speed) as WatchSpeed);
    if (action === 'push') controller.resume();
    if (action === 'drill') controller.drill();
    if (action === 'cancel-drill') controller.stopDrill();
    if (action === 'preview') controller.previewAway(Number(button.dataset.ms));
    if (action === 'spend') {
      if (suppressClick) {
        suppressClick = false;
        return;
      }
      controller.spendPoints(button.dataset.slot as SlotId, spendAmount(button));
    }
    if (action === 'rebraid') controller.rebraid();
    if (action === 'open-rules') controller.openRules();
    if (action === 'rules-back') controller.closeRules();
    if (action === 'rule-add') controller.addRule();
    if (action === 'rule-remove') controller.removeRule(index);
    if (action === 'rule-up') controller.moveRule(index, -1);
    if (action === 'rule-down') controller.moveRule(index, 1);
    if (action === 'equip' && button.dataset.id) controller.equip(button.dataset.id);
    if (action === 'unequip') controller.unequip(button.dataset.slot as GearSlot);
    if (action === 'salvage' && button.dataset.id) controller.salvage(button.dataset.id);
    if (action === 'enhance') controller.enhance(button.dataset.slot as GearSlot);
    if (action === 'lock' && button.dataset.id) controller.lockItem(button.dataset.id);
    if (action === 'unlock' && button.dataset.id) controller.unlockItem(button.dataset.id);
    if (action === 'bag-filter' && button.dataset.slot) controller.setBagFilter(button.dataset.slot as BagFilter);
    if (action === 'bag-sort') controller.toggleBagSort();
    if (action === 'recycle-bulk') controller.recycleBulk();
    if (action === 'play-here') void controller.playHere();
    if (action === 'reset' && window.confirm('Erase this braid and start again?')) controller.resetSave();
  };
  const onStatus = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!button) return;
    if (button.dataset.action === 'sign-in-google') void controller.signInWithGoogle();
    if (button.dataset.action === 'sign-out') void controller.signOutAccount();
    if (button.dataset.action === 'report') controller.openFeedback();
  };
  const onPanelChange = (event: Event): void => {
    const input = event.target as HTMLElement;
    const action = input.dataset.action;
    const index = Number(input.dataset.index ?? '-1');
    if (action === 'set-spec') {
      const value = (input as HTMLSelectElement).value;
      controller.setSpec(input.dataset.slot as SlotId, value ? (value as SubSpecId) : null);
    }
    if (action === 'rule-ability') controller.setRuleAbility(index, (input as HTMLSelectElement).value);
    if (action === 'rule-target') controller.setRuleTarget(index, (input as HTMLSelectElement).value as TargetKind);
    if (action === 'rule-condition') {
      controller.setRuleCondition(index, (input as HTMLSelectElement).value as Condition['kind']);
    }
    if (action === 'rule-param') {
      const rule = controller.save.data.hero.rules[index];
      if (!rule) return;
      controller.setRuleCondition(index, rule.condition.kind, Number((input as HTMLInputElement).value));
    }
    if (action === 'recycle-below') controller.setRecycleBelow((input as HTMLSelectElement).value as Rarity);
    if (action === 'auto-recycle') {
      const value = (input as HTMLSelectElement).value;
      controller.setAutoRecycle(value ? (value as Rarity) : null);
    }
  };
  const onModal = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!button) return;
    if (button.dataset.action === 'keep-google') {
      void controller.keepGoogleSave();
      return;
    }
    if (button.dataset.action === 'stay-guest') {
      controller.stayGuest();
      return;
    }
    if (button.dataset.action !== 'dismiss') return;
    if (button.classList.contains('modal-backdrop') && event.target !== button) return;
    controller.dismissModal();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !controller.rulesOpen) return;
    event.preventDefault();
    controller.closeRules();
  };
  const onPop = (): void => {
    if (!controller.rulesOpen) return;
    controller.closeRules(true);
  };
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      stopHold();
      controller.hide();
    } else controller.show();
  };
  const onPageHide = (): void => {
    stopHold();
    controller.hide();
  };
  const onBlur = (): void => stopHold();

  let hold: { pointerId: number; slot: SlotId; count: number | 'max'; delay: number; timer: ReturnType<typeof setTimeout> } | null = null;
  let suppressClick = false;

  const stopHold = (): void => {
    if (!hold) return;
    clearTimeout(hold.timer);
    hold = null;
  };
  const heldButton = (): HTMLButtonElement | null => {
    if (!hold) return null;
    const count = hold.count === 'max' ? 'max' : String(hold.count);
    return panel.querySelector<HTMLButtonElement>(
      `button[data-action="spend"][data-slot="${hold.slot}"][data-count="${count}"]`,
    );
  };
  const repeatHold = (): void => {
    const button = heldButton();
    if (!hold || !button || button.disabled) {
      stopHold();
      return;
    }
    controller.spendPoints(hold.slot, hold.count);
    hold.delay = Math.max(SPEND_HOLD_MIN_MS, Math.floor(hold.delay * 0.72));
    hold.timer = setTimeout(repeatHold, hold.delay);
  };
  const onPointerDown = (event: PointerEvent): void => {
    const button = spendButton(event.target);
    if (!button || button.disabled || event.button !== 0) return;
    event.preventDefault();
    stopHold();
    suppressClick = true;
    const slot = button.dataset.slot as SlotId;
    const count = spendAmount(button);
    controller.spendPoints(slot, count);
    hold = {
      pointerId: event.pointerId,
      slot,
      count,
      delay: SPEND_HOLD_START_MS,
      timer: setTimeout(repeatHold, SPEND_HOLD_START_MS),
    };
    try {
      button.setPointerCapture(event.pointerId);
    } catch {
      // A redraw can drop the button before capture sticks. Window pointerup still ends the hold.
    }
  };
  const onPointerUp = (event: PointerEvent): void => {
    if (!hold || event.pointerId !== hold.pointerId) return;
    stopHold();
    window.setTimeout(() => {
      suppressClick = false;
    }, 0);
  };
  const onContextMenu = (event: Event): void => {
    if (!spendButton(event.target)) return;
    event.preventDefault();
  };
  const onSelectStart = (event: Event): void => {
    if (!spendButton(event.target)) return;
    event.preventDefault();
  };

  const topbar = document.querySelector<HTMLElement>('#topbar');
  const onResize = (): void => syncRulesSheetTop();
  const topbarObserver =
    topbar && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncRulesSheetTop()) : null;
  if (topbarObserver && topbar) topbarObserver.observe(topbar);
  window.addEventListener('resize', onResize);

  tabs.addEventListener('click', onTab);
  panel.addEventListener('click', onPanelClick);
  panel.addEventListener('pointerdown', onPointerDown);
  panel.addEventListener('contextmenu', onContextMenu);
  panel.addEventListener('selectstart', onSelectStart);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);
  window.addEventListener('blur', onBlur);
  status.addEventListener('click', onStatus);
  panel.addEventListener('change', onPanelChange);
  modal.addEventListener('click', onModal);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('keydown', onKey);
  window.addEventListener('popstate', onPop);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    unsub();
    unmountDialog();
    tabs.removeEventListener('click', onTab);
    stopHold();
    panel.removeEventListener('click', onPanelClick);
    panel.removeEventListener('pointerdown', onPointerDown);
    panel.removeEventListener('contextmenu', onContextMenu);
    panel.removeEventListener('selectstart', onSelectStart);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    window.removeEventListener('blur', onBlur);
    status.removeEventListener('click', onStatus);
    panel.removeEventListener('change', onPanelChange);
    modal.removeEventListener('click', onModal);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('popstate', onPop);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('resize', onResize);
    topbarObserver?.disconnect();
    document.documentElement.style.removeProperty('--topbar-bottom');
  };
}

function spendButton(target: EventTarget | null): HTMLButtonElement | null {
  if (!(target instanceof Element)) return null;
  const button = target.closest('button[data-action="spend"]');
  return button instanceof HTMLButtonElement ? button : null;
}

function spendAmount(button: HTMLElement): number | 'max' {
  const raw = button.dataset.count ?? '1';
  if (raw === 'max') return 'max';
  const count = Number(raw);
  return Number.isInteger(count) && count > 0 ? count : 1;
}
